<script lang="ts">
	import { Label, Spinner, Text, Icon } from '@Shelly/shelly-design-system';
	import { createEventDispatcher } from 'svelte';
	import {
		getSubtitleTextSize,
		getSubtitleClasses,
	} from '@Shelly/shelly-design-system/AdvancedCard/advanced-card-helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import {
		defaultSubtitlesProps,
		defaultOptionalProps,
	} from '@Shelly/shelly-design-system/WidgetCard/widget-card-helpers';
	import { load_image_from } from 'src/helpers/interface_helpers';

	// Props consistent with WidgetCard
	export let title: string = '';
	export let subtitle: {
		value?: string;
		type?: string;
		textSmall?: boolean;
		icon?: string;
		bold?: boolean;
	} = { ...defaultSubtitlesProps };
	export let description: {
		value?: string;
		type?: string;
		textSmall?: boolean;
		icon?: string;
		bold?: boolean;
	} = { ...defaultOptionalProps };
	export let image: string = '';
	export let editMode: boolean = false;
	export let selected: boolean = false;
	export let disabled: boolean = false;
	export let isLoading: boolean = false;
	export let hideSlotBottom: boolean = false;
	export let hideSlotMain: boolean = false;
	export let widgetCardRef: HTMLElement;
	export let minHeight: number | undefined;
	type LabelType =
		| 'premium'
		| 'default'
		| 'primary'
		| 'secondary'
		| 'success'
		| 'warning'
		| 'danger'
		| 'disabled'
		| 'primary-10'
		| 'success-10'
		| 'warning-10'
		| 'premium-10'
		| 'color-0'
		| 'color-2'
		| 'ai'
		| 'experimental';
	type LabelSize = 's' | 'xs' | 'm' | 'l' | 'xl';

	export let leftBadge: {
		icon?: string;
		value?: string;
		type?: LabelType;
		size?: LabelSize;
	} = null;

	const dispatch = createEventDispatcher();

	$: subtitle = {
		...defaultSubtitlesProps,
		...subtitle,
	};

	$: description = {
		...defaultOptionalProps,
		...description,
	};

	$: classListSubtitle = classMap(getSubtitleClasses(subtitle as any));
	$: classListDesc = classMap(getSubtitleClasses(description as any));

	$: hasTitle = title;
	$: hasSubtitle = subtitle.value;
	$: hasDescription = description.value;
	$: hasMainSlot = $$slots.main && !hideSlotMain;

	const CAMERA_WIDGET_MIN_HEIGHT = 180;
	const CAMERA_WIDGET_MIN_HEIGHT_VIEW = 220;

	$: cardStyle = `min-height:${Math.max(
		minHeight ?? 0,
		editMode ? CAMERA_WIDGET_MIN_HEIGHT : CAMERA_WIDGET_MIN_HEIGHT_VIEW,
	)}px;`;

	// Use camera image
	$: imageSrc = image || load_image_from('images/device_images/logo_white_S.png');

	const handleImageError = () => {
		imageSrc = load_image_from('images/device_images/logo_white_S.png');
	};

	function handleClick(event: MouseEvent) {
		dispatch('click', event);
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			dispatch('click', event);
		}
	}
</script>

<div
	bind:this={widgetCardRef}
	style={cardStyle}
	class="camera-widget-card position-relative d-flex flex-direction-column width-full"
	class:selected
	class:disabled
	class:loading={isLoading}
	class:edit-mode={editMode}
	on:click={handleClick}
	on:keydown={handleKeydown}
	role="button"
	tabindex="0"
>
	<!-- Background Image (camera preview) -->
	<div class="camera-image-container position-absolute">
		<img
			src={imageSrc}
			alt={title}
			class="camera-image width-full"
			class:disabled
			on:error={handleImageError}
		/>

		{#if isLoading}
			<div
				class="loading-overlay position-absolute d-flex justify-content-center align-items-start"
			>
				<Spinner size="m" />
			</div>
		{/if}
	</div>

	<!-- Gradient Overlay -->
	<div class="gradient-overlay position-absolute"></div>

	<!-- Top Badges -->
	<div class="badges-container position-absolute">
		{#if !editMode}
			<div class="d-flex">
				{#if leftBadge}
					<Label
						icon={leftBadge.icon}
						value={leftBadge.value}
						type={leftBadge.type || 'default'}
						size={leftBadge.size || 's'}
						noRadiusType="second"
					/>
				{:else}
					<slot name="leftBadge" />
				{/if}
			</div>
		{/if}
	</div>

	<!-- Content Area -->
	<div
		class={classMap({
			'content-container position-relative d-flex flex-direction-column flex-1 justify-content-end': true,
		})}
	>
		<div class="text-content d-flex flex-direction-column align-items-start text-left width-full">
			{#if hasTitle}
				<Text class="title text-bold" styleType="body-small">{title}</Text>
			{/if}

			{#if hasSubtitle}
				<div
					class={classMap({
						'd-flex align-items-center width-full': true,
						'mt-xs': hasTitle,
					})}
				>
					<Text
						class={classListSubtitle}
						styleType={getSubtitleTextSize(subtitle.textSmall)}
						noWrap
					>
						{subtitle.value}
					</Text>
				</div>
			{/if}

			{#if hasDescription}
				<div
					class={classMap({
						'description-container d-flex align-items-center': true,
						'mt-xs': hasTitle || hasSubtitle,
					})}
				>
					{#if description.icon}
						<Icon
							size="xs"
							icon={description.icon}
							class={classMap({ 'mr-xs': true, [classListDesc]: true })}
						/>
					{/if}
					<Text class={classListDesc} styleType={getSubtitleTextSize(description.textSmall)}>
						{description.value}
					</Text>
				</div>
			{/if}
		</div>

		<!-- Main Slot (e.g., Motion label) -->
		{#if hasMainSlot && !editMode}
			<div class="main-slot d-flex justify-content-start width-full pt-s">
				<slot name="main" />
			</div>
		{/if}
	</div>
	<!-- Bottom Slot (e.g., Edit actions) -->
	{#if $$slots.bottom && !hideSlotBottom}
		<div class="bottom-slot-container mt-s">
			<slot name="bottom" />
		</div>
	{/if}
</div>

<style>
	.position-absolute {
		position: absolute;
	}
	.camera-widget-card {
		width: 100%;
		height: 100%;
		border-radius: var(--radius-big);
		overflow: hidden;
		cursor: pointer;
		background: var(--color-1);
		padding: var(--spacing-s);
	}

	.camera-widget-card .description-container {
		min-height: 32px;
	}

	.camera-widget-card.edit-mode {
		cursor: default;
	}

	.camera-widget-card.edit-mode::after {
		content: '';
		position: absolute;
		width: 100%;
		height: 100%;
		left: 0;
		top: 0;
		background: var(--color-primary);
		opacity: 0.1;
		pointer-events: none;
		z-index: 1;
	}
	.camera-widget-card.selected,
	.camera-widget-card:focus:not(.edit-mode) {
		box-shadow: var(--shadow-glow-strong);
	}

	.camera-widget-card.selected::before,
	.camera-widget-card:focus:not(.edit-mode)::before {
		content: '';
		position: absolute;
		left: 0;
		top: 0;
		border-radius: var(--radius-big);
		width: calc(100% - 4px);
		height: calc(100% - 4px);
		border: 2px solid var(--color-primary);
		pointer-events: none;
		z-index: 3;
	}

	.camera-image-container {
		top: -8px;
		left: -8px;
		width: calc(100% + 16px);
		height: calc(100% + 16px);
		z-index: 0;
	}

	.camera-image {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.camera-image.loading,
	.camera-image.disabled {
		filter: grayscale(100%);
		opacity: 0.6;
	}

	.loading-overlay {
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		padding-top: 30%;
		background-color: rgba(0, 0, 0, 0.4);
		z-index: 1;
	}

	.gradient-overlay {
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0) 0%,
			rgba(0, 0, 0, 0.05) 40%,
			rgba(0, 0, 0, 0.55) 64%,
			rgba(0, 0, 0, 0.9) 100%
		);
		z-index: 0;
	}

	.badges-container {
		top: 0;
		left: 0;
		z-index: 2;
	}

	.content-container {
		z-index: 1;
	}

	.bottom-slot-container {
		z-index: 2;
		margin-bottom: calc(-1 * var(--spacing-s));
		margin-left: calc(-1 * var(--spacing-s));
		margin-right: calc(-1 * var(--spacing-s));
		cursor: default;
		overflow: hidden;
	}

	.camera-widget-card :global(.bottom-slot-container .actions-container) {
		width: 100%;
		justify-content: center;
		border-radius: 0 !important;
	}
</style>
